<script setup lang="ts">
import { useAuthUser } from '@/helpers/globals'
import type { ObjektRahmen } from '@/models/apollon_conf_fk/ObjektRahmen'
import type { ProjektFeld } from '@/models/apollon_conf_fk/ProjektFeld'
import type { LiegenschaftData } from '@/models/apollon_data/LiegenschaftData'
import { Gesuch } from '@/models/Gesuch'
import { Liegenschaft } from '@/models/Liegenschaft'
import { createModel } from '@kp/js-model'
import dayjs from 'dayjs'
import { onMounted, ref, type Ref } from 'vue'

const {
    gesuch,
    liegenschaft = null,
    readOnly = true,
} = defineProps<{
    gesuch: Gesuch
    liegenschaft?: LiegenschaftData
    readOnly?: boolean
}>()
const loading = ref(true)
const liegenschaftEntry: Ref<Liegenschaft | null> = ref(null)
const zusatzdatenEntry: Ref<{ [key: string]: { value: number | string | null, display: string | null }
}> = ref({})
const zusatzdaten: Ref<
    { label: string | null; value: { value: string | number | null; display: string | null } }[]
> = ref([])
const initialLoading = ref(false)

const benutzer = useAuthUser()

onMounted(() => {
    initialLoading.value = true
    liegenschaftEntry.value = createModel(Liegenschaft, liegenschaft?.liegenschaft || undefined)
    extractZusatzdatenConfig()
    zusatzdatenEntry.value = { ...liegenschaft?.liegenschaft_zusatzdaten }
    loading.value = false
    initialLoading.value = false
})
function canRemove() {
    return benutzer?.id === liegenschaft?.liegenschaft?.ersteller_id
}
function extractZusatzdatenConfig() {
    if (!liegenschaft?.liegenschaft_zusatzdaten) {
        liegenschaft?.set('liegenschaft_zusatzdaten', [])
    }
    const rahmen = gesuch?.config?.config?.objekte?.rahmen || {}
    for (const r in rahmen) {
        if (isComponentVisible(rahmen[r]) && r !== 'objekt_felder') {
            for (const feld in rahmen[r].felder) {
                if (isComponentVisible(rahmen[r].felder[feld]) && rahmen[r].felder[feld].required) {
                    zusatzdaten.value.push({
                        label: rahmen[r].felder[feld].label,
                        value:
                            rahmen[r]?.felder[feld]?.feld_name &&
                            liegenschaft?.liegenschaft_zusatzdaten[rahmen[r].felder[feld].feld_name]
                                ? liegenschaft.liegenschaft_zusatzdaten[
                                      rahmen[r].felder[feld].feld_name
                                  ]
                                : { value: null, display: null },
                    })
                }
            }
        }
    }
}
function isComponentVisible(comp: ObjektRahmen | ProjektFeld) {
    let visible = true
    if (gesuch.phase === 'einreichung') {
        if (comp.einreichung_from) {
            visible = visible && dayjs(comp.einreichung_from).isBefore(dayjs())
        }
        if (comp.einreichung_to) {
            visible = visible && dayjs().isBefore(dayjs(comp.einreichung_to).add(1, 'day'))
        }
        return visible && comp.einreichung
    }
    if (gesuch.phase === 'abschluss') {
        if (comp.abschluss_from) {
            visible = visible && dayjs(comp.abschluss_from).isBefore(dayjs())
        }
        if (comp.abschluss_to) {
            visible = visible && dayjs().isBefore(dayjs(comp.abschluss_to).add(1, 'day'))
        }
        return visible && comp.abschluss
    }
    return false
}
</script>

<template>
    <v-card :loading="loading" tile>
        <v-card-title v-if="liegenschaftEntry">
            <div class="title-container">
                <div>
                    {{ liegenschaftEntry.strasse }} {{ liegenschaftEntry.strassen_nummer }}<br />
                    {{ liegenschaftEntry.ort_plz }} {{ liegenschaftEntry.ort }}
                </div>
                <v-spacer />
                <v-btn
                    variant="text"
                    size="x-small"
                    color="primary"
                    @click.stop="$emit('edit-liegenschaft-zusatzdaten', liegenschaft)"
                    :icon="readOnly ? 'fas fa-eye' : 'fas fa-pen'"
                >
                </v-btn>
                <v-btn
                    variant="text"
                    icon="fas fa-times"
                    size="x-small"
                    color="stop"
                    :disabled="!canRemove() || readOnly"
                    @click.stop="$emit('delete-liegenschaft', liegenschaftEntry.id)"
                >
                </v-btn>
            </div>
        </v-card-title>
        <v-card-text v-if="liegenschaftEntry">
            {{ $t('gemeinde') }}: {{ liegenschaftEntry.gemeinde_bezeichnung }}<br />
            {{ $t('baujahr') }}: {{ liegenschaftEntry.baujahr }}<br />
            {{ $t('egid') }}: {{ liegenschaftEntry.egid }}<br />
            {{ $t('parzellen_nummer') }}: {{ liegenschaftEntry.parzellen_nummer }}<br />
            {{ $t('assekuranz_nummer') }}: {{ liegenschaftEntry.assekuranz_nummer }}<br />
        </v-card-text>
        <v-divider
            v-if="gesuch?.config?.config?.objekte?.visible && zusatzdaten.length > 0"
            class="mx-4"
        ></v-divider>
        <v-card-text v-if="gesuch?.config?.config?.objekte?.visible && zusatzdaten.length > 0">
            <div v-for="data in zusatzdaten" :key="data.label || 'new'">
                <span>{{ data.label }}: </span>
                <span v-if="data.value && typeof data.value === 'object'">{{
                    data.value.display
                }}</span>
                <span v-else-if="data.value">{{ data.value }}</span>
                <span v-else style="color: red">{{ $t('value_missing') }}</span>
            </div>
        </v-card-text>
    </v-card>
</template>

<style lang="css" scoped>
.title-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>
